import React, {useRef} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {Dialog} from '@rn-vui/themed';

import {
  GiftCardEntryFormData,
  giftCardEntrySchema,
} from '../../../models/schemas/giftCardsSchemaZod';
import TextInput from '../../ui-controls/form/TextInput';
import Text from '../../ui-controls/Text';

const errorWithFirstLetterCapitalized = (error?: string) =>
  error ? error.charAt(0).toUpperCase() + error.slice(1) : undefined;

type ZenGiftCardEntryModalProps = {
  isVisible: boolean;
  onCancel: () => void;
  onAdd: (formValues: GiftCardEntryFormData) => void;
  isSubmitting: boolean;
  error?: string;
};

export const ZenGiftCardEntryModal = ({
  isVisible,
  isSubmitting,
  onCancel,
  onAdd,
  error,
}: ZenGiftCardEntryModalProps) => {
  const pinInput = useRef<typeof TextInput>(null);

  const {
    handleSubmit,
    formState: {errors, touchedFields},
    setValue,
    watch,
    reset,
  } = useForm<GiftCardEntryFormData>({
    resolver: zodResolver(giftCardEntrySchema),
    defaultValues: {number: '', pin: ''},
    mode: 'onTouched', // Validates after first blur, then on change
  });

  const numberValue = watch('number');
  const pinValue = watch('pin');

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isVisible) {
      reset({number: '', pin: ''});
    }
  }, [isVisible, reset]);

  // Handler to mark field as touched on blur (RHF needs this for touchedFields)
  const handleFieldBlur = (fieldName: 'number' | 'pin') => () => {
    setValue(fieldName, fieldName === 'number' ? numberValue : pinValue, {shouldTouch: true});
  };

  // Everything below Dialog.Title. Identical on every platform (DIS-1550).
  const dialogContent = (
    <>
      <TextInput
        accessibilityLabel="Card number"
        autoFocus={true}
        errorMessageWhenTouched={errors.number?.message}
        keyboardType="numeric"
        label="Card number"
        onBlur={handleFieldBlur('number')}
        onChangeText={(text: string) => setValue('number', text, {shouldValidate: true})}
        onSubmitEditing={() => pinInput.current?.focus()}
        placeholder="Enter the card number"
        returnKeyType="next"
        touched={!!touchedFields.number}
        value={numberValue}
      />
      <TextInput
        accessibilityLabel="PIN"
        errorMessageWhenTouched={errors.pin?.message}
        keyboardType="numeric"
        label="PIN"
        onBlur={handleFieldBlur('pin')}
        onChangeText={(text: string) => setValue('pin', text, {shouldValidate: true})}
        onSubmitEditing={handleSubmit(onAdd)}
        placeholder="Enter the PIN"
        ref={pinInput}
        returnKeyType="done"
        touched={!!touchedFields.pin}
        value={pinValue}
      />
      {!!error && <Text style={{color: 'red'}}>{errorWithFirstLetterCapitalized(error)}</Text>}
      <Dialog.Actions>
        <Dialog.Button disabled={isSubmitting} onPress={onCancel} title="CANCEL" type="outline" />
        <Dialog.Button
          disabled={isSubmitting}
          loading={isSubmitting}
          onPress={handleSubmit(onAdd)}
          title="ADD"
          type="solid"
        />
      </Dialog.Actions>
    </>
  );

  return (
    // DIS-1716: no manual keyboard compensation here. The @rn-vui Overlay this
    // Dialog renders into already wraps its content in a KeyboardAvoidingView
    // (behavior "height" on Android, "padding" on iOS), and that component is
    // driven by JS keyboardDidShow/Hide events rather than by window resize, so
    // it is functional on Android regardless of windowSoftInputMode. DIS-1692
    // added a second lift on top of it; the two stacked and pinned the dialog to
    // the top edge of the screen. One compensation is enough — do not add another.
    <Dialog isVisible={isVisible} onBackdropPress={onCancel}>
      <Dialog.Title title="Add a Gift Card" />
      {dialogContent}
    </Dialog>
  );
};
